import dynamic from "next/dynamic";
const App = dynamic(() => import("../../../components/App"));
import React, { useEffect, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {
  Breadcrumb,
  notification,
  Space,
  Typography,
  Row,
  Col,
  Button,
  Spin,
  Modal,
  Radio,
  Table,
  Empty,
  Divider,
  Input,
  Form,
} from "antd";
import {
  CheckOutlined,
  FrownOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import ImageGallery from "react-image-gallery";
import Countdown from "react-countdown";
import Image from "next/image";

import { Api } from "../../../networks/Api";
import { convert } from "../../../utils/convert";
import { getCartData } from "../../../redux/actions/cartActions";
import { setItemWithExpiry } from "../../../utils/localstorage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { viewContent, addToCart } from "../../../utils/facebookPixel";

dayjs.extend(utc);
dayjs.extend(timezone);

const ProductSlide = dynamic(
  () => import("../../../components/ProductSlideScroll"),
);
import ThumborJS from "thumborjs";

const imageUrl = process.env.NEXT_PUBLIC_IMAGE_URL;
const thumborKey = process.env.NEXT_PUBLIC_THUMBOR_KEY;
const thumborUrl = process.env.NEXT_PUBLIC_THUMBOR_URL;

const { Title, Text, Paragraph } = Typography;

const getProductImageUrl = (image = {}) => {
  if (image?.imagepath_compress) {
    const imagePath = image.imagepath_compress.replace(/uploads\//g, "");
    return new ThumborJS(thumborUrl, thumborKey)
      .imgpath(`${imageUrl}/${imagePath}`)
      .genurl();
  }

  return (
    image?.imagepath ||
    `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`
  );
};

const buildGalleryImages = (data) => {
  const images = (data?.product?.product_image || []).map((image) => {
    const src = getProductImageUrl(image);
    return {
      original: src,
      thumbnail: src,
      originalAlt: data?.product?.name,
      thumbnailAlt: data?.product?.name,
      imagepath: image?.imagepath,
    };
  });

  if (images.length) return images;

  const src = getProductImageUrl(data?.product);
  return [{ original: src, thumbnail: src }];
};

// ลบ HTML tag + decode entity ออกจาก rich-text ของ CMS
// ใช้กับ meta description / og / twitter และ structured data
// สำคัญ: ต้อง decode &lt; &gt; "ก่อน" ลบ tag เพื่อรองรับกรณี markup ถูก escape มา
// (เช่น &lt;ul&gt;&lt;li&gt;... ที่มาจาก CMS) ไม่งั้น tag จะโผล่ค้างใน description
const stripHtml = (html) =>
  (html || "")
    // 0) decode &amp; ก่อน รองรับ markup ที่ถูก escape หลายชั้น (&amp;lt; -> &lt;, &amp;nbsp; -> &nbsp;)
    .replace(/&amp;/gi, "&")
    // 1) decode &lt; / &gt; ให้กลายเป็น tag จริง (รองรับ escaped markup)
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    // 2) ลบ tag ทั้งหมด (ครอบคลุมทั้ง tag จริงและที่เพิ่ง decode)
    .replace(/<[^>]*>/g, " ")
    // 3) decode entity อื่น ๆ ที่เหลือ
    .replace(/&nbsp;/gi, " ")
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;|&rsquo;|&lsquo;/gi, "'")
    .replace(/&mdash;/gi, "—")
    .replace(/&ndash;/gi, "–")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10))) // numeric entity
    // 4) จัด whitespace
    .replace(/\s+/g, " ")
    .trim();

// ตัดความยาวข้อความแบบไม่ตัดกลางคำ (สำหรับ meta description)
const truncateText = (text, max = 160) => {
  const value = (text || "").trim();
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim()}...`;
};

// สร้าง JSON-LD schema.org/Product จาก productData (ทำงานฝั่ง server ได้ ไม่พึ่ง client state)
// ใช้สกุลเงินฐาน THB เพื่อให้ราคา/ความพร้อมจำหน่ายอยู่ใน HTML ที่ serve เสมอ
const buildProductJsonLd = ({
  productData,
  brandList,
  imageUrlMain,
  pageUrl,
  locale,
}) => {
  const product = productData?.product;
  if (!product) return null;

  const price = product?.discount
    ? product?.discount?.["thb"]?.sellprice
    : product?.currency?.["thb"]?.sellprice;

  const images = [];
  (product?.product_image || []).forEach((v) => {
    // ปรับตรงนี้
    const image_path = v?.imagepath_compress?.replace(/uploads\//g, "");
    const url = `${imageUrl}/${image_path}`;

    const imageProduct = v?.imagepath_compress
      ? url
      : v?.imagepath
        ? v?.imagepath
        : `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`;

    if (v?.imagepath) images.push(imageProduct);
  });
  if (!images.length && (product?.imagepath || imageUrlMain)) {
    images.push(product?.imagepath || imageUrlMain);
  }

  const brandName =
    brandList?.find((b) => String(b?.number) === String(product?.brand_code))
      ?.name ||
    product?.brand_name ||
    product?.brand?.name_en;

  const inStock = (productData?.productList || []).some(
    (p) => parseInt(p?.availablestock) >= 1,
  );
  const isPreOrder =
    product?.published_at && new Date() < new Date(product.published_at);

  const description = stripHtml(
    locale === "th" ? product?.description : product?.description_en,
  );

  // fallback description กรณี CMS ไม่ได้กรอก (กัน Google Merchant warning "Missing field description")
  const productName = product?.product_name || product?.name || "";
  const fallbackDescription = [brandName, productName, product?.category]
    .filter(Boolean)
    .join(" - ");
  const finalDescription = description || fallbackDescription;

  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: productName,
    ...(images.length ? { image: images } : {}),
    description: finalDescription,
    sku: String(product?.sku || product?.id || ""),
    ...(brandName ? { brand: { "@type": "Brand", name: brandName } } : {}),
  };

  // ใส่ offers เฉพาะเมื่อมีราคา และไม่ใช่สินค้า "สอบถามราคา"
  if (price && !product?.is_price_on_request) {
    jsonLd.offers = {
      "@type": "Offer",
      url: pageUrl,
      priceCurrency: "THB",
      price: Number(price).toFixed(2),
      availability: isPreOrder
        ? "https://schema.org/PreOrder"
        : inStock
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        merchantReturnLink:
          "https://luxury29.io/info/product-exchange-return-policy",
        applicableCountry: "TH",
        returnPolicyCategory:
          "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 2,
      },
      shippingDetails: [
        {
          "@type": "OfferShippingDetails",
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "TH",
            addressRegion: [
              "Bangkok",
              "Nonthaburi",
              "Pathum Thani",
              "Samut Prakan",
              "Samut Sakhon",
              "Nakhon Pathom",
            ],
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: 1,
              maxValue: 3,
              unitCode: "DAY",
            },
          },
        },
        {
          "@type": "OfferShippingDetails",
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "TH",
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: 3,
              maxValue: 7,
              unitCode: "DAY",
            },
          },
        },
      ],
    };
  }

  return jsonLd;
};

// สร้าง BreadcrumbList JSON-LD (Home > แบรนด์/หมวด > สินค้า) ให้ SERP โชว์ breadcrumb trail แทน URL เปล่า
// ใช้ค่าเดียวกับ breadcrumb ที่แสดงบนหน้า (category จาก URL param) และรองรับ locale prefix (/th)
const buildBreadcrumbJsonLd = ({
  base,
  locale,
  category,
  productName,
  productPath,
}) => {
  if (!category) return null;
  const prefix = locale === "th" ? "/th" : "";
  const itemListElement = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${base}${prefix}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: category,
      item: `${base}${prefix}/${encodeURIComponent(category)}`,
    },
  ];
  if (productName) {
    itemListElement.push({
      "@type": "ListItem",
      position: 3,
      name: productName,
      item: `${base}${prefix}${productPath}`,
    });
  }
  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement,
  };
};

export async function getServerSideProps(ctx) {
  let productData, productProfileData;
  const token = ctx?.req?.cookies?.token || null;

  try {
    let channel = "website";
    if (token) {
      try {
        const profileResponse = await axios.get(
          Api.UserProfile,
          Api.Header(token),
        );
        productProfileData = profileResponse?.data?.data;
        channel = productProfileData?.channel || "website";
      } catch (e) {
        // profile fail ไม่ block — ใช้ default channel
      }
    }

    // ให้ SSR รอเฉพาะข้อมูลที่จำเป็นต่อ first paint เท่านั้น รายการแบรนด์ สี
    // และสินค้าแนะนำโหลดภายหลังฝั่ง client เพื่อไม่ให้ API ที่ช้าที่สุดถ่วง TTFB
    const productRes = await axios.get(
      `${Api.getProductDetail}/${ctx.params.id}?channel=${channel}`,
      Api.Header(),
    );
    productData = productRes?.data?.data;
  } catch (error) {
    console.log(error);
  }

  return {
    props: {
      ...(await serverSideTranslations(ctx?.locale, ["common"])),
      token,
      id: ctx.params.id,
      category: ctx.params.category,
      subcategory: ctx.params.subcategory,
      locale: ctx?.locale,
      productData: productData || null,
      productProfileData: productProfileData || null,
    },
  };
}

const style_available_number = {
  backgroundColor: "black",
  color: "white",
  display: "flex",
  flexDirection: "column",
  width: "45px",
  height: "45px",
  lineHeight: "1rem",
};

const Home = ({
  token,
  locale,
  id,
  category,
  subcategory,
  productData,
  productProfileData,
  categoryMenu,
  hasSale,
  hasLive,
  dataSeo,
  dataReferrer,
}) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const router = useRouter();
  let queryParam = router?.query?.aid ? `aid=${router?.query?.aid}` : ``;
  queryParam = router?.query?.pid
    ? `${queryParam}&pid=${router?.query?.pid}`
    : queryParam;

  queryParam = router?.query?.pf
    ? `${queryParam}pf=${router?.query?.pf}`
    : queryParam;

  if (router?.query?.utm_source) {
    queryParam = queryParam
      ? `${queryParam}&utm_source=${router?.query?.utm_source}`
      : `?utm_source=${router?.query?.utm_source}`;
  }

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openNotify, setOpenNotify] = useState(false);
  const [openInterest, setOpenInterest] = useState(false);
  const [colorValue, setColorValue] = useState([]);
  const [sizeValue, setSizeValue] = useState([]);
  const [colorSelect, setColorSelect] = useState();
  const [sizeSelect, setSizeSelect] = useState();
  // seed จาก productData (ฐาน THB) เพื่อให้ราคา/ชื่อโผล่ใน SSR HTML ไม่ใช่ 0/ว่าง
  const [productPrice, setProductPrice] = useState(() => {
    const p = productData?.product;
    if (!p) return 0;
    return (
      (p?.discount
        ? p?.discount?.["thb"]?.sellprice
        : p?.currency?.["thb"]?.sellprice) || 0
    );
  });
  const [productName, setProductName] = useState(
    productData?.product?.product_name || productData?.product?.name || "",
  );
  const [availablestock, setAvailablestock] = useState([]);
  const [feedProduct, setFeedProduct] = useState();
  const [imageList, setImageList] = useState(() =>
    buildGalleryImages(productData),
  );
  const [colorAll, setColorAll] = useState([]);
  const thumbnailPosition = "left";
  const [canAddToBag, setCanAddToBag] = useState(true);
  const [categoryData, setCategoryData] = useState([]);
  const [subCategoryData, setSubCategoryData] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [sellPrice, setSellPrice] = useState(0);
  const [discountEnd, setDiscountEnd] = useState("");
  const [brandData, setBrandData] = useState([]);
  const profileData = productProfileData || {};
  const [success, setSuccess] = useState(false);

  const redux_fingerprint = useSelector((state) => state?.fingerprint);
  const { currencyData } = useSelector((state) => state?.currency);
  const { currencyAllData } = useSelector((state) => state?.currencyAll);
  const viewedProduct = productData?.product;
  const is_notify =
    viewedProduct?.published_at &&
    new Date() < new Date(viewedProduct.published_at);

  useEffect(() => {
    if (!viewedProduct?.id) return;

    viewContent(viewedProduct);
  }, [viewedProduct]);

  const openNotificationError = (msg) => {
    notification.open({
      message: "Notice",
      description: msg,
      placement: "top",
    });
  };

  const changProductName = (input) => {
    let name = input;
    if (name) {
      name = name?.split("(");
      name?.pop();
      name = name.join(" (");
    }
    if (!name) name = input;

    return name;
  };

  const showModal = () => {
    setOpen(true);
  };

  const hideModal = () => {
    setOpen(false);
  };

  const showModalInterest = () => {
    setOpenInterest(true);
  };

  const hideModalInterest = () => {
    setOpenInterest(false);
  };

  const handleNotify = async (values) => {
    // { email: "test@example.com" }

    try {
      // เรียก API
      const postData = {
        email: values.email,
        product_id: productData?.product?.id,
      };
      const response = await axios.post(
        Api.createProductNotification,
        postData,
        Api.Header(),
      );

      if (response?.status == 200) {
        // แสดงหน้า Success
        setSuccess(true);
      }
    } catch (error) {
      console.error("Something went wrong");
    }
  };

  useEffect(() => {
    const { query } = router;
    const categoryPrev = window.localStorage.getItem("categoryPrev");
    if (categoryPrev) {
      setItemWithExpiry("productPrev", query.category);
    }
  }, [router]);

  useEffect(() => {
    let cancelled = false;

    const fetchLookupData = async () => {
      const [brandRes, colorRes] = await Promise.allSettled([
        axios.get(Api.getFeedBrandList, Api.Header()),
        axios.get(Api.getColorList, Api.Header()),
      ]);
      if (cancelled) return;

      if (brandRes.status === "fulfilled") {
        const result = [];
        (brandRes.value?.data?.data || []).forEach((v) => {
          result[v?.number?.toString()] = v?.name;
        });
        setBrandData(result);
      }

      if (colorRes.status === "fulfilled") {
        const result = [];
        (colorRes.value?.data?.data || []).forEach((v) => {
          result[v?.number] = v;
        });
        setColorAll(result);
      }
    };

    fetchLookupData().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let default_size = "";
      let default_color = "";

      try {
        if (productData?.product) {
          const datas = productData;
          const name = changProductName(datas?.product?.name);
          // setProductData(datas);
          setProductName(name);
          setSizeSelect("");
          setColorSelect("");
          setCanAddToBag(false);
          let result_size = [];
          let result_color = [];
          let result_image = [];
          let result_stock = [];
          let result_productname = [];
          const productList = datas?.productList;

          /* Start Discount */
          let result_discount = datas?.product?.discount
            ? datas?.product?.discount[currencyData]?.discount
            : 0;
          /* End Discount */
          let sellprice_before_discount = datas?.product?.discount
            ? datas?.product?.discount[currencyData]?.sellprice_before_discount
            : 0;

          let sellprice = datas?.product?.discount
            ? datas?.product?.discount[currencyData]?.sellprice
            : datas?.product?.currency[currencyData]?.sellprice || 0;

          setSellPrice(sellprice_before_discount);

          if (result_discount) {
            setDiscount(result_discount);
            setDiscountEnd(
              datas?.product?.web_promotion_product?.web_promotion?.ended_at,
            );
          } else {
            setDiscount(0);
            setDiscountEnd("");
          }
          setProductPrice(sellprice);

          const product_image = datas?.product?.product_image;
          let imagepath;
          await productList?.map(async (product) => {
            if (parseInt(product?.availablestock) >= 1) {
              setCanAddToBag(true);
            }
            if (product?.imagepath_compress) {
              const image_path = product?.imagepath_compress?.replace(
                /uploads\//g,
                "",
              );
              const tugen = new ThumborJS(thumborUrl, thumborKey);
              const url = tugen.imgpath(`${imageUrl}/${image_path}`).genurl();
              // imagepath = product?.imagepath;
              imagepath = product?.imagepath_compress
                ? url
                : product?.imagepath
                  ? product?.imagepath
                  : `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`;
            }
            // ใช้เช็คว่ามีจำนวนสินค้าไหม และใช้ดึง product id
            if (product?.variant?.length == 1) {
              result_stock[product?.variant[0].name] = product;
            }
            if (product?.variant?.length == 2) {
              if (product?.variant[0].variant_name == "Size") {
                result_stock[
                  [`${product?.variant[0].name}-${product?.variant[1].name}`]
                ] = product;
              } else {
                result_stock[
                  [`${product?.variant[1].name}-${product?.variant[0].name}`]
                ] = product;
              }
            }

            // ใช้ใน select list
            await product?.variant?.map(async (v) => {
              if (v?.variant_name == "Size") {
                result_size[v?.name] = {
                  ...v,
                  product,
                };
                if (parseInt(product?.availablestock) >= 1) {
                  default_size = v?.name;
                }
              } else if (v?.variant_name == "Colour") {
                result_color[v?.name] = {
                  ...v,
                  product,
                };
                if (parseInt(product?.availablestock) >= 1) {
                  default_color = v?.name;
                }
                result_productname[v?.name] = product;
              }
            });
          });

          await Promise.all(
            await product_image?.map(async (v) => {
              const image_path = v?.imagepath_compress?.replace(
                /uploads\//g,
                "",
              );
              const tugen = new ThumborJS(thumborUrl, thumborKey);
              const url = tugen.imgpath(`${imageUrl}/${image_path}`).genurl();

              const imageProduct = v?.imagepath_compress
                ? url
                : v?.imagepath
                  ? v?.imagepath
                  : `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`;

              result_image.push({
                original: imageProduct,
                thumbnail: imageProduct,
                originalAlt: datas?.product?.name,
                thumbnailAlt: datas?.product?.name,
                imagepath: v?.imagepath,
              });
            }),
          );

          if (result_image?.length == 0) {
            if (imagepath) {
              result_image.push({
                original: imagepath,
                thumbnail: imagepath,
              });
            } else {
              result_image.push({
                original: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`,
                thumbnail: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`,
              });
            }
          }

          const clothsize = Object.keys(result_size);
          const ORDER = [
            "one size",
            "xxs",
            "xs",
            "s",
            "m",
            "l",
            "xl",
            "2xl",
            "3xl",
            "4xl",
            "5xl",
          ];
          clothsize.sort();
          clothsize.sort((a, b) => {
            a = a.toLowerCase();
            b = b.toLowerCase();

            let nra = parseInt(a);
            let nrb = parseInt(b);

            if (ORDER.indexOf(a) != -1) nra = NaN;
            if (ORDER.indexOf(b) != -1) nrb = NaN;

            if (nrb === 0) return 1;
            if ((nra && !nrb) || nra === 0) return -1;
            if (!nra && nrb) return 1;
            if (nra && nrb) {
              if (nra == nrb) {
                return a
                  .substr(("" + nra).length)
                  .localeCompare(a.substr(("" + nra).length));
              } else {
                return nra - nrb;
              }
            } else {
              return ORDER.indexOf(a) - ORDER.indexOf(b);
            }
          });

          setAvailablestock(result_stock);
          // result_image.sort((a, b) => {
          //   if (b.imagepath < a.imagepath) return -1;
          //   if (b.imagepath > a.imagepath) return 1;
          //   return 0;
          // });
          setImageList(result_image);
          setSizeValue(clothsize);
          setColorValue(result_color);
          if (productList?.length == 1) {
            setSizeSelect(default_size);
            setColorSelect(default_color);
          } else {
            if (Object.keys(result_color).length == 1) {
              setColorSelect(default_color);
            }
          }
        }
      } catch (e) {
        openNotificationError(e?.message);
        console.log("fetchData >> getProductDetail error :", e);
      }
    };

    fetchData().catch(console.error);
  }, [id, currencyData, productData]);

  useEffect(() => {
    const productId = productData?.product?.id;
    if (!productId) return;

    let cancelled = false;
    const fetchRecommendedProducts = async () => {
      try {
        const response = await axios.get(
          `${Api.getFeedProductList}?page=1&size=10&product_id=${productId}`,
          Api.Header(),
        );
        if (cancelled || response?.status != 200) return;

        const categories = [];
        (response.data?.group_category || []).forEach((v) => {
          categories[String(v?.number)] = v?.name_en;
        });
        const subCategories = [];
        (response.data?.sub_category || []).forEach((v) => {
          subCategories[String(v?.group_category_code) + String(v?.number)] =
            v?.name;
        });

        setCategoryData(categories);
        setSubCategoryData(subCategories);
        setFeedProduct(response.data?.dataWithColor || []);
      } catch (error) {
        console.error("Error fetching recommended products:", error);
      }
    };

    fetchRecommendedProducts();
    return () => {
      cancelled = true;
    };
  }, [productData?.product?.id]);

  const onChangeSize = (value) => {
    if (value.target.value == sizeSelect) {
      setSizeSelect("");
      setColorSelect("");
      setCanAddToBag(true);
    } else {
      setSizeSelect(value.target.value);
      let size = value.target.value;

      let result = 0;
      Object.keys(colorValue)?.map((color) => {
        // console.log(size, color);
        let product = availablestock[`${size}-${color}`];
        if (parseInt(product?.availablestock) >= 1) {
          result = 1;
        }
        if (product) {
          const name = changProductName(product?.name);
          /* Start Discount */
          let result_discount = product?.discount
            ? product?.discount[currencyData]?.discount
            : 0;
          /* End Discount */
          let sellprice_before_discount = product?.discount
            ? product?.discount[currencyData]?.sellprice_before_discount
            : 0;

          let sellprice = product?.discount
            ? product?.discount[currencyData]?.sellprice
            : product?.currency[currencyData]?.sellprice || 0;

          setSellPrice(sellprice_before_discount);

          if (result_discount) {
            setDiscount(result_discount);
            setDiscountEnd(
              product?.web_promotion_product?.web_promotion?.ended_at,
            );
          } else {
            setDiscount(0);
            setDiscountEnd("");
          }
          setProductPrice(sellprice);
          setProductName(name);
        }
      });
      if (result == 0) {
        setCanAddToBag(false);
      } else {
        setCanAddToBag(true);
      }
    }
  };

  const onChangeColor = async (value, product) => {
    if (value == colorSelect) {
      setColorSelect("");
      setSizeSelect("");
      setCanAddToBag(true);
    } else {
      setLoading(true);
      setColorSelect(value);
      let result_image = [];

      if (product?.product_image?.length > 0) {
        await product?.product_image?.map(async (v) => {
          const image_path = v?.imagepath_compress?.replace(/uploads\//g, "");
          const tugen = new ThumborJS(thumborUrl, thumborKey);
          const url = tugen.imgpath(`${imageUrl}/${image_path}`).genurl();
          const imageProduct = v?.imagepath_compress
            ? url
            : v?.imagepath
              ? v?.imagepath
              : `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`;
          result_image.push({
            original: imageProduct,
            thumbnail: imageProduct,
          });
        });
      }

      if (!result_image?.length) {
        result_image.push({
          original: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`,
          thumbnail: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpeg`,
        });
      }
      setImageList(result_image);
      setLoading(false);

      let color = value;
      let result = 0;
      sizeValue?.map((size) => {
        let product = availablestock[`${size}-${color}`];
        if (parseInt(product?.availablestock) >= 1) {
          result = 1;
        }
        if (product) {
          const name = changProductName(product?.name);

          /* Start Discount */
          let result_discount = product?.discount
            ? product?.discount[currencyData]?.discount
            : 0;
          /* End Discount */
          let sellprice_before_discount = product?.discount
            ? product?.discount[currencyData]?.sellprice_before_discount
            : 0;

          let sellprice = product?.discount
            ? product?.discount[currencyData]?.sellprice
            : product?.currency[currencyData]?.sellprice || 0;

          setSellPrice(sellprice_before_discount);

          if (result_discount) {
            setDiscount(result_discount);
            setDiscountEnd(
              product?.web_promotion_product?.web_promotion?.ended_at,
            );
          } else {
            setDiscount(0);
            setDiscountEnd("");
          }
          setProductPrice(sellprice);
          setProductName(name);
        }
      });
      if (result == 0) {
        setCanAddToBag(false);
      } else {
        setCanAddToBag(true);
      }
    }
  };

  function gtag_report_conversion() {
    const url = window.location.href;
    var callback = function () {
      if (typeof url != "undefined") {
        // window.location = url;
      }
    };
    // gtag("event", "conversion", {
    //   send_to: "AW-11311855547/Ils1CNCgvtwYELvv9JEq",
    //   event_callback: callback,
    // });
    return false;
  }

  const handleAddToCart = async (checkout = false) => {
    let indexValue = "";
    if (sizeSelect) indexValue = sizeSelect;
    if (colorSelect) {
      if (indexValue == "") indexValue = colorSelect;
      else indexValue = `${indexValue}-${colorSelect}`;
    }
    const product_id = availablestock[indexValue]?.id;

    if (parseInt(availablestock[indexValue]?.availablestock) < 1)
      openNotificationError("Out of stock.");
    else if (!product_id) {
      openNotificationError("Please select size, color.");
    } else {
      setLoading(true);
      let channel = "website";
      if (profileData?.channel == "line") channel = "line";

      const postData = {
        uuid: redux_fingerprint.fingerprintData,
        product_id,
        number: 1,
        channel,
      };
      try {
        const url = `${Api.addCart}`;
        const response = await axios.post(url, postData, Api.Header());
        if (response?.status == 200) {
          const url_cart = `${Api.getCartList}`;
          const response_cart = await axios.post(
            url_cart,
            postData,
            Api.Header(token),
          );
          dispatch(getCartData(response_cart?.data?.data));

          const selectedProduct = availablestock[indexValue];
          if (selectedProduct) addToCart(selectedProduct);

          if (checkout) {
            // gtag_report_conversion();
            router.push(`/checkout${queryParam ? `?${queryParam}` : ``}`);
          } else {
            notification.open({
              type: "success",
              message: `${t("product-add-to-bag")}`,
              description: `${t("success-text")}`,
              placement: "topRight",
            });
            setLoading(false);
          }
        }
      } catch (e) {
        console.log(e);
        openNotificationError(e?.response?.data?.data || e?.message);
        setLoading(false);
        console.log("postData >> addCart error :", e);
      }
    }
  };

  const number0beforedigit = (input) => {
    return ("0" + input).slice(-2);
  };

  const renderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      // Render a completed state
      // return <Completionist />;
    } else {
      // Render a countdown
      if (days > 0) {
        return (
          <span>
            {t("ends-in")}
            {` `}
            <span style={{ fontKerning: `none` }}>
              <Space direction="horizontal" size={3}>
                <div
                  style={{
                    fontSize: "140%",
                    fontWeight: "bold",
                    letterSpacing: `0.1rem`,
                  }}
                >
                  {days}
                </div>
                <div>Days</div>
              </Space>
            </span>
          </span>
        );
      } else {
        return (
          <span>
            {t("ends-in")}
            {` `}
            <span style={{ fontKerning: `none`, letterSpacing: `0.1rem` }}>
              <Space direction="horizontal" size={3}>
                <div className="number-clock">{number0beforedigit(hours)}</div>:
                <div className="number-clock">
                  {number0beforedigit(minutes)}
                </div>
                :
                <div className="number-clock">
                  {number0beforedigit(seconds)}
                </div>
              </Space>
            </span>
          </span>
        );
      }
    }
  };

  const avariableRenderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      // Render a completed state
      // return <Completionist />;
    } else {
      return (
        <span>
          <p
            className="mb-1"
            style={{ fontSize: "18px", color: "rgb(245, 34, 45)" }}
          >
            {t("available-in")}
          </p>
          {` `}
          <span style={{ fontKerning: `none` }}>
            <Space direction="horizontal" size={3}>
              <div className="number-clock" style={style_available_number}>
                <p>{days}</p>
                <p>Days</p>
              </div>
              :
              <div className="number-clock" style={style_available_number}>
                {number0beforedigit(hours)}
                <p>Hrs</p>
              </div>
              :
              <div className="number-clock" style={style_available_number}>
                {number0beforedigit(minutes)}
                <p>Min</p>
              </div>
              :
              <div className="number-clock" style={style_available_number}>
                {number0beforedigit(seconds)}
                <p>Sec</p>
              </div>
            </Space>
          </span>
        </span>
      );
    }
  };

  const dataSource = [
    {
      key: "1",
      woman: "3.5",
      man: "2",
      eu: "34",
    },
    {
      key: "2",
      woman: "4",
      man: "2.5",
      eu: "34.5",
    },
    {
      key: "3",
      woman: "4.5",
      man: "3",
      eu: "35",
    },
    {
      key: "4",
      woman: "5",
      man: "3.5",
      eu: "35.5",
    },
    {
      key: "5",
      woman: "5.5",
      man: "4",
      eu: "36",
    },
    {
      key: "6",
      woman: "6",
      man: "4.5",
      eu: "36.5",
    },
    {
      key: "7",
      woman: "6.5",
      man: "5",
      eu: "37.5",
    },
    {
      key: "8",
      woman: "7",
      man: "5.5",
      eu: "38",
    },
    {
      key: "9",
      woman: "7.5",
      man: "6",
      eu: "38.5",
    },
    {
      key: "10",
      woman: "8",
      man: "6.5",
      eu: "39",
    },
    {
      key: "11",
      woman: "8.5",
      man: "7",
      eu: "40",
    },
    {
      key: "12",
      woman: "9",
      man: "7.5",
      eu: "40.5",
    },
    {
      key: "13",
      woman: "9.5",
      man: "8",
      eu: "41",
    },
    {
      key: "14",
      woman: "10",
      man: "8.5",
      eu: "42",
    },
    {
      key: "15",
      woman: "10.5",
      man: "9",
      eu: "42.5",
    },
    {
      key: "16",
      woman: "11",
      man: "9.5",
      eu: "43",
    },
    {
      key: "17",
      woman: "11.5",
      man: "10",
      eu: "44",
    },
    {
      key: "18",
      woman: "12",
      man: "10.5",
      eu: "44.5",
    },
    {
      key: "19",
      woman: "12.5",
      man: "11",
      eu: "45",
    },
    {
      key: "20",
      woman: "13",
      man: "11.5",
      eu: "45.5",
    },
    {
      key: "21",
      woman: "13.5",
      man: "12",
      eu: "46",
    },
    {
      key: "22",
      woman: "14",
      man: "12.5",
      eu: "47",
    },
    {
      key: "23",
      woman: "15",
      man: "13",
      eu: "47.5",
    },
    {
      key: "24",
      woman: "16",
      man: "13.5",
      eu: "48",
    },
  ];

  const columns = [
    {
      title: "US Women",
      dataIndex: "woman",
      key: "woman",
      align: "center",
      width: "33.33%",
    },
    {
      title: "US Men",
      dataIndex: "man",
      key: "man",
      align: "center",
      width: "33.33%",
    },
    {
      title: "EU",
      dataIndex: "eu",
      key: "eu",
      align: "center",
      width: "33.33%",
    },
  ];

  const image_path = productData?.product?.imagepath_compress?.replace(
    /uploads\//g,
    "",
  );
  const tugen = new ThumborJS(thumborUrl, thumborKey);
  const imageurl = tugen.imgpath(`${imageUrl}/${image_path}`).genurl();

  const canonicalUrl = `${process.env.NEXT_PUBLIC_FRONTEND_URL}${
    (router.asPath || "").split("?")[0]
  }`;
  const productJsonLd = buildProductJsonLd({
    productData,
    brandList: Object.keys(brandData).map((number) => ({
      number,
      name: brandData[number],
    })),
    imageUrlMain: imageurl,
    pageUrl: canonicalUrl,
    locale,
  });

  const breadcrumbJsonLd = buildBreadcrumbJsonLd({
    base: process.env.NEXT_PUBLIC_FRONTEND_URL || "",
    locale,
    category,
    productName:
      productData?.product?.product_name || productData?.product?.name,
    productPath: (router.asPath || "").split("?")[0],
  });

  // meta description ที่สะอาด (ไม่มี HTML markup) + fallback เป็นแบรนด์ + ชื่อสินค้า
  const cleanDescription = stripHtml(
    locale === "th"
      ? productData?.product?.description
      : productData?.product?.description_en,
  );
  const metaBrandName =
    brandData?.[productData?.product?.brand_code] ||
    productData?.product?.brand_name ||
    productData?.product?.brand?.name_en;
  const metaFallback = [
    metaBrandName,
    productData?.product?.product_name || productData?.product?.name,
  ]
    .filter(Boolean)
    .join(" ");
  const metaDescription = truncateText(cleanDescription || metaFallback, 160);

  // ชื่อแบรนด์สำหรับลิงก์ (fallback กัน /undefined เมื่อ brandData ยังไม่โหลด)
  const brandName =
    brandData?.[productData?.product?.brand_code] ||
    productData?.product?.brand_name ||
    productData?.product?.brand?.name_en ||
    "";

  const ogLocale = locale === "th" ? `th_TH` : `en_US`;
  const path = router.asPath.split("?")[0];
  const localePath = router.locale === "th" ? `/th${path}` : path;

  if (productData?.product)
    return (
      <App
        token={token}
        categoryMenu={categoryMenu}
        hasSale={hasSale}
        hasLive={hasLive}
        dataSeo={dataSeo}
        dataReferrer={dataReferrer}
      >
        <Head>
          <title>{productData?.product?.product_name}</title>
          {productJsonLd && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(productJsonLd),
              }}
            />
          )}
          {breadcrumbJsonLd && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(breadcrumbJsonLd),
              }}
            />
          )}
          <meta name="title" content={productData?.product?.product_name} />
          <meta name="description" content={metaDescription} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={productData?.product?.product_name}
          />
          <meta name="twitter:description" content={metaDescription} />
          <meta
            name="twitter:image"
            content={`${productData?.product?.imagepath || imageurl}`}
          />

          <meta property="fb:app_id" content="1686331181830759" />
          <meta property="og:type" content="article" />
          <meta
            property="og:site_name"
            content={productData?.product?.product_name}
          />
          <meta
            property="og:title"
            content={productData?.product?.product_name}
          />
          <meta
            property="og:image:alt"
            content={productData?.product?.product_name}
          />
          <meta
            property="og:image"
            content={`${productData?.product?.imagepath || imageurl}`}
          />
          <meta property="og:image:width" content="1000" />
          <meta property="og:image:height" content="1000" />
          <meta property="og:image:type" content="image/png" />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:locale" content={ogLocale} />
          <meta
            property="og:url"
            content={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${localePath}`}
          />
        </Head>
        <Spin spinning={loading}>
          <Modal
            className="size-guide"
            title="Size Guide"
            open={open}
            onOk={hideModal}
            onCancel={hideModal}
            footer={null}
          >
            <Table
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              size={"small"}
              bordered
            />
          </Modal>

          {/* Interest card */}
          <Modal
            className="size-guide"
            title={t("interest-card")}
            open={openInterest}
            onOk={hideModalInterest}
            onCancel={hideModalInterest}
            footer={null}
          >
            <Space direction="vertical" className="w-100">
              <Paragraph
                style={{
                  margin: 0,
                  color: "#555",
                  lineHeight: 1.5,
                }}
              >
                {t("interest-card-detail")}
              </Paragraph>
              <Button
                id="buy_now"
                className="btn-buy w-100 mt-2"
                style={{ color: "#ffffff" }}
                block
                size="large"
                href={`https://line.me/R/ti/p/@440mrrpd`}
                icon={
                  <Image
                    src="/static/images/ico/ic_line-outline.png"
                    alt="line"
                    width={20}
                    height={20}
                    quality={75}
                    unoptimized
                  />
                }
              >
                {t("contact")}
              </Button>
              <Divider plain style={{ margin: 0 }}>
                Or
              </Divider>
              <Button
                block
                size="large"
                href="mailto:lxy36cardshop@gmail.com"
                className="outline-btn"
                icon={
                  <Image
                    src="/static/images/ico/ic_email-outline.png"
                    alt="email"
                    width={20}
                    height={20}
                    quality={75}
                    unoptimized
                  />
                }
                style={{
                  justifyContent: "center",
                  border: "1px solid #000000",
                  borderRadius: "4px",
                }}
              >
                lxy36cardshop@gmail.com
              </Button>
              <Button
                block
                size="large"
                href="tel:+66802651979"
                className="outline-btn"
                icon={
                  <Image
                    src="/static/images/ico/ic_phone-outline.png"
                    alt="phone"
                    width={20}
                    height={20}
                    quality={75}
                    unoptimized
                  />
                }
                style={{
                  justifyContent: "center",
                  border: "1px solid #000000",
                  borderRadius: "4px",
                }}
              >
                +6680 265 1979
              </Button>
            </Space>
          </Modal>

          {/* Notify me */}
          <Modal
            open={openNotify}
            onCancel={() => {
              setOpenNotify(false);
            }}
            footer={null}
            className="size-guide"
          >
            <Form layout="vertical" onFinish={handleNotify}>
              <div className="text-center">
                <Title
                  level={4}
                  style={{
                    marginBottom: 12,
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  {t("be-the-first")}
                </Title>

                <Text strong>{productData?.product?.name}</Text>
                <br />
                <Text>
                  {t("launching")}{" "}
                  {dayjs(productData?.product?.published_at).format(
                    "D MMMM YYYY HH:mm",
                  )}{" "}
                  {t("nationwide")}
                </Text>
              </div>

              <Form.Item
                name="email"
                style={{ marginTop: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Please enter a valid email address.",
                  },
                  {
                    type: "email",
                    message: "Invalid email address.",
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter your email address."
                  disabled={success}
                />
              </Form.Item>

              <Button
                htmlType="submit"
                type="primary"
                block
                size="large"
                disabled={success}
                style={{
                  background: "#000",
                  borderColor: "#000",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {t("notify-me")}
              </Button>

              {success && (
                <Text
                  style={{
                    display: "block",
                    marginTop: 12,
                    textAlign: "center",
                  }}
                >
                  {t("notify-soon")}
                </Text>
              )}
            </Form>
          </Modal>

          <Row style={{ marginBottom: 24 }} justify="center">
            <Col xs={22} sm={22} md={22} lg={22} xl={20}>
              {profileData?.channel == "line" ? (
                <></>
              ) : (
                <Breadcrumb
                  items={[
                    {
                      key: "home",
                      title: (
                        <Link href={`/${queryParam ? `?${queryParam}` : ``}`}>
                          {t("home")}
                        </Link>
                      ),
                    },
                    ...(category
                      ? [
                          {
                            key: "category",
                            title: (
                              <Link
                                href={`/${category}${
                                  queryParam ? `?${queryParam}` : ``
                                }`}
                              >
                                {category}
                              </Link>
                            ),
                          },
                        ]
                      : []),
                    ...(subcategory
                      ? [{ key: "subcategory", title: subcategory }]
                      : []),
                    {
                      key: "title",
                      title: productName,
                    },
                  ]}
                />
              )}

              <Row
                gutter={[24, 24]}
                style={{ marginTop: 24, marginBottom: 24 }}
                justify="center"
              >
                {/* Thumbnail */}
                <Col xs={24} sm={24} md={14} lg={14} xl={16}>
                  <Row>
                    <Col xs={0} sm={0} md={24} lg={24} xl={24}>
                      <div>
                        <ImageGallery
                          thumbnailPosition={thumbnailPosition}
                          showBullets={false}
                          infinite={true}
                          lazyLoad={true}
                          showFullscreenButton={true}
                          showGalleryFullscreenButton={false}
                          showPlayButton={false}
                          showGalleryPlayButton={false}
                          showNav={true}
                          items={imageList}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={24} md={0} lg={0} xl={0}>
                      <ImageGallery
                        thumbnailPosition="bottom"
                        showBullets={false}
                        infinite={true}
                        lazyLoad={true}
                        showFullscreenButton={true}
                        showGalleryFullscreenButton={false}
                        showPlayButton={false}
                        showGalleryPlayButton={false}
                        showNav={true}
                        items={imageList}
                      />
                    </Col>
                  </Row>
                </Col>
                {/* Detail */}
                <Col xs={24} sm={24} md={10} lg={10} xl={8}>
                  <Row justify={"center"}>
                    <div style={{ width: "100%", maxWidth: 420 }}>
                      <Row justify={`space-between`}>
                        {productData?.product?.group_category_code == "07" ? (
                          <Col>
                            <p
                              className="h2"
                              style={{
                                color: "#8E8E8E",
                                fontSize: "16px",
                                lineHeight: "1.5",
                                cursor: "pointer",
                              }}
                            >
                              {productData?.product?.category}
                            </p>
                          </Col>
                        ) : (
                          <Col>
                            {brandName ? (
                              <Link
                                href={`/${encodeURIComponent(brandName)}${
                                  queryParam ? `?${queryParam}` : ``
                                }`}
                              >
                                <p
                                  className="h2"
                                  style={{
                                    color: "#8E8E8E",
                                    fontSize: "16px",
                                    lineHeight: "1.5",
                                    cursor: "pointer",
                                  }}
                                >
                                  {brandName}
                                </p>
                              </Link>
                            ) : null}
                          </Col>
                        )}
                      </Row>
                      <p
                        className="h2"
                        style={{
                          fontSize: "20px",
                          lineHeight: "1.4",
                        }}
                      >
                        {productName}
                      </p>
                      <div
                        style={{
                          marginTop: "1.2em",
                        }}
                      >
                        {discount != 0 && (
                          <div className="price-promotion-container">
                            <Text
                              style={{
                                fontWeight: 400,
                                fontSize: "12px",
                                marginBottom: 0,
                              }}
                              delete
                            >{`${
                              currencyAllData?.[currencyData]?.symbol || ""
                            } ${convert(sellPrice)}`}</Text>
                            {productData?.product?.web_promotion_product
                              ?.web_promotion?.discount_percent && (
                              <p className="promotion-container">
                                {
                                  productData?.product?.web_promotion_product
                                    ?.web_promotion?.discount_percent
                                }
                                % Off
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      {!productData?.product?.is_price_on_request ? (
                        <Space
                          direction="horizontal"
                          style={{
                            width: "100%",
                            justifyContent: "space-between",
                            marginBottom: "7px",
                          }}
                        >
                          {productPrice && (
                            <p
                              className="h4"
                              style={
                                discount != 0
                                  ? { marginTop: 0, color: "rgb(245, 34, 45)" }
                                  : {}
                              }
                            >
                              {currencyAllData[currencyData]?.symbol} {` `}
                              {convert(productPrice)}
                            </p>
                          )}
                          {discountEnd && (
                            <Countdown
                              date={dayjs(discountEnd) + 10000}
                              renderer={renderer}
                            />
                          )}
                        </Space>
                      ) : (
                        <></>
                      )}

                      {is_notify && (
                        <Space
                          direction="horizontal"
                          className="mb-2"
                          style={{
                            width: "100%",
                            justifyContent: "space-between",
                            marginBottom: "7px",
                          }}
                        >
                          {productData?.product?.published_at && (
                            <Countdown
                              date={
                                dayjs(productData?.product?.published_at) +
                                10000
                              }
                              renderer={avariableRenderer}
                            />
                          )}
                        </Space>
                      )}

                      <Space direction="horizontal" className="space-buy">
                        {profileData?.channel == "line" || !canAddToBag ? (
                          <></>
                        ) : (
                          <>
                            {!["07"].includes(
                              productData?.product?.group_category_code,
                            ) &&
                              !productData?.product?.is_price_on_request &&
                              !is_notify && (
                                <Button
                                  id="add_to_cart"
                                  className="btn-add"
                                  block
                                  size="large"
                                  disabled={!canAddToBag}
                                  onClick={() => {
                                    const product_id = sizeSelect
                                      ? sizeSelect
                                      : colorSelect
                                        ? sizeSelect
                                        : productData?.product?.id;
                                    handleAddToCart();
                                  }}
                                >
                                  {canAddToBag ? (
                                    <>{t("product-add-to-bag")}</>
                                  ) : (
                                    <>{t("out-of-stock")}</>
                                  )}
                                </Button>
                              )}
                          </>
                        )}

                        {!productData?.product?.is_price_on_request &&
                        !is_notify ? (
                          <Button
                            id="buy_now"
                            className="btn-buy"
                            block
                            size="large"
                            disabled={!canAddToBag}
                            onClick={() => {
                              const product_id = sizeSelect
                                ? sizeSelect
                                : colorSelect
                                  ? sizeSelect
                                  : productData?.product?.id;
                              handleAddToCart(true);
                            }}
                          >
                            {canAddToBag ? (
                              <>BUY NOW</>
                            ) : (
                              <>{t("out-of-stock")}</>
                            )}
                          </Button>
                        ) : (
                          <>
                            {is_notify ? (
                              <Button
                                id="buy_now"
                                className="btn-buy"
                                block
                                size="large"
                                onClick={() => {
                                  setOpenNotify(true);
                                }}
                              >
                                {t("notify-me")}
                              </Button>
                            ) : (
                              <Button
                                id="buy_now"
                                className="btn-buy"
                                block
                                size="large"
                                onClick={() => {
                                  setOpenInterest(true);
                                }}
                              >
                                {t("check-latest-price")}
                              </Button>
                            )}
                          </>
                        )}
                      </Space>

                      {canAddToBag &&
                        productData?.product?.group_category_code != "11" && (
                          <>
                            {(productData?.product?.hide_size &&
                              sizeValue.length == 1) ||
                              (sizeValue.length > 0 && (
                                <div style={{ marginBlock: 24 }}>
                                  <Row justify={`space-between`}>
                                    <Col>
                                      <p className="h5">
                                        {t("product-select-size")}
                                      </p>
                                    </Col>
                                    <Col>
                                      {productData?.product
                                        ?.group_category_code == "05" && (
                                        <p
                                          onClick={showModal}
                                          className="h5 link-black"
                                        >
                                          {t("product-size-guide")}
                                        </p>
                                      )}
                                    </Col>
                                  </Row>
                                  <Radio.Group
                                    className="select-size"
                                    onChange={onChangeSize}
                                    value={sizeSelect}
                                  >
                                    {sizeValue?.map((size) => {
                                      const v = size;
                                      const disable =
                                        (parseInt(
                                          availablestock[`${v}-${colorSelect}`]
                                            ?.availablestock,
                                        ) < 1 ||
                                          !availablestock[
                                            `${v}-${colorSelect}`
                                          ]) &&
                                        colorSelect
                                          ? true
                                          : false;
                                      return (
                                        <Radio
                                          key={v}
                                          value={v}
                                          onClick={onChangeSize}
                                          disabled={disable}
                                        >
                                          <div
                                            className={
                                              disable
                                                ? `select-size-radio disable`
                                                : `select-size-radio`
                                            }
                                          >
                                            {v}
                                          </div>
                                        </Radio>
                                      );
                                    })}
                                  </Radio.Group>
                                </div>
                              ))}

                            {(productData?.product?.hide_color &&
                              Object.keys(colorValue).length == 1) ||
                              (Object.keys(colorValue).length > 0 && (
                                <div style={{ marginBlock: 24 }}>
                                  <Row>
                                    <Col>
                                      <p className="h5">
                                        {t("product-select-color")}
                                      </p>
                                    </Col>
                                  </Row>
                                  <Radio.Group
                                    className="select-color"
                                    value={colorSelect}
                                  >
                                    {Object.keys(colorValue)?.map((image) => {
                                      const v = colorValue[image];
                                      const disable =
                                        (parseInt(
                                          availablestock[
                                            `${sizeSelect}-${v?.name}`
                                          ]?.availablestock,
                                        ) < 1 ||
                                          !availablestock[
                                            `${sizeSelect}-${v?.name}`
                                          ]) &&
                                        sizeSelect
                                          ? true
                                          : false;
                                      return (
                                        <Radio
                                          key={v?.name}
                                          value={v?.name}
                                          onClick={() =>
                                            onChangeColor(v?.name, v?.product)
                                          }
                                          disabled={disable}
                                        >
                                          <div
                                            className={
                                              disable
                                                ? `select-color-radio disable`
                                                : `select-color-radio`
                                            }
                                          >
                                            <div
                                              className="color"
                                              style={{
                                                backgroundColor: `${
                                                  colorAll[
                                                    v?.product?.color_code
                                                  ]?.color_code || "#f6f6f6"
                                                }`,
                                              }}
                                            ></div>
                                            <CheckOutlined />
                                          </div>
                                        </Radio>
                                      );
                                    })}
                                  </Radio.Group>
                                </div>
                              ))}
                          </>
                        )}
                    </div>
                  </Row>
                </Col>
                <Col xs={22} sm={22} md={24} lg={20} xl={20}>
                  {productData?.product?.description &&
                    productData?.product?.description_en && (
                      <>
                        <p className="h5" style={{ marginBottom: "24px" }}>
                          {t("product-description")}
                        </p>
                        <div
                          id="product_description"
                          style={{ lineHeight: "1.4rem" }}
                          dangerouslySetInnerHTML={{
                            __html:
                              locale == "th"
                                ? productData?.product?.description || "-"
                                : productData?.product?.description_en || "-",
                          }}
                        />
                      </>
                    )}
                </Col>
              </Row>
              {profileData?.channel == "line" ? (
                <></>
              ) : (
                <>
                  {feedProduct?.length > 0 && (
                    <>
                      <div className="border-t border-gray-200 my-5" />
                      <p className="h5" style={{ textAlign: "center" }}>
                        {t("product-you-may-also-like")}
                      </p>
                      <div>
                        <ProductSlide
                          t={t}
                          products={feedProduct}
                          url={`type=all`}
                          categoryData={categoryData}
                          subCategoryData={subCategoryData}
                          brandData={brandData}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Spin>
      </App>
    );
  else
    return (
      <App
        token={token}
        categoryMenu={categoryMenu}
        hasSale={hasSale}
        hasLive={hasLive}
        dataSeo={dataSeo}
        dataReferrer={dataReferrer}
      >
        <Row style={{ marginTop: 24, marginBottom: 24 }} justify="center">
          {/* Detail */}
          <Col xs={24} sm={24} md={10} lg={10} xl={8}>
            <Row justify={"center"}>
              <Col>
                <Empty description={`${t("no-product")}`} />
              </Col>
            </Row>
          </Col>
        </Row>
      </App>
    );
};

export default Home;
